<template>
    <div class="p-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
        <button
          @click="$router.push('/ratelimits')"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Rate Limits
        </button>
        {{ isNewOrg ? "Add Rate Limit" : "Edit Rate Limit" }}
        <div class="relative group">
        <button
            type="button"
            class="hover:bg-transparent bg-transparent ml-2"
            @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>
  
      <div v-if="loading" class="text-center py-4">
        <p class="text-gray-600">Loading...</p>
      </div>

      <HowToRateLimit v-if="showHowToPage"
        :show-how-to-page="showHowToPage"
        :external-navigation="true"
        @show-how-to-close="handleShowHowToPage"
      />
  
      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>
  
      <form v-else @submit.prevent="saveRateLimit" class="space-y-6 p-6 glass-card">
        <div>
          <label
            for="organization"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Organisation</label
          >
          <GlassSelect
            id="organization"
            v-model="ratelimit.organization"
            @focus="clearFieldError('organization')"
            :options="orgs"
            placeholder="Select an Organisation"
            :error="!!fieldErrors['organization']"
          />
          <FieldError :message="fieldErrors['organization']"/>
        </div>
  
        <div>
          <label
            for="max_concurrent_updates"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Max Concurrent Updates</label
          >
          <input
            type="number"
            id="max_concurrent_updates"
            v-model="ratelimit.max_concurrent_updates"
            placeholder="100"
            @focus="clearFieldError('max_concurrent_updates')"
            :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['max_concurrent_updates']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors['max_concurrent_updates']"/>
        </div>
  
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="$router.push({ name: 'RateLimitsView' })"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button v-if="isStaff || canAddRateLimit || canChangeRateLimit"
            type="submit"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {{ isNewOrg ? "Create Rate Limit" : "Save Changes" }}
          </button>
        </div>
      </form>
    </div>
  </template>
  
  <script>

  import { createRateLimit, updateRateLimit,getRateLimitConfigs } from "@/services/wsRateLimitSevice";
  import GlassSelect from "@/components/GlassSelect.vue";
  import { getOrgs } from "@/services/wsOrgService";
  import { useStore } from "@/store";
  import FieldError from "@/components/FieldError.vue";
  import HowToRateLimit from "./HowToRateLimit.vue";
  import { useTutorial } from "@/composables/tutorial";

  export default {
    name: "RateLimitDetails",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    components: {
      FieldError,
      HowToRateLimit,
      GlassSelect,
    },
    data() {
      return {
        ratelimit: {
          organization: null,
          max_concurrent_updates: "",
        },
        showHowToPage: false,
        loading: false,
        error: null,
        fieldErrors: {},
        isNewOrg: false,
        orgs: [],
        canChangeRateLimit: false,
        canAddRateLimit: false,
        steps: [
          {
            element: '#organization',
            action: 'select',
            value: 1,
            message: 'Select the organization for this rate limit.'
          },
          {
            element: '#max_concurrent_updates',
            action: 'type',
            value: '500',
            message: 'Enter the maximum number of concurrent updates allowed.'
          },
          {
            element: 'button[type="submit"]',
            action: 'display',
            value: null,
            message: 'Click to create the rate limit or save your changes.'
          }
        ],
      };
    },
    computed: {
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      }, 
      formattedRateLimit() {
        return {
          ...this.ratelimit
        };
      },
    },
    async created() {
      const ratelimitId = this.$route.params.id;
      this.checkPermissions();
      
      if (ratelimitId === "new") {
        this.isNewOrg = true;
        await this.fetchOrg()
      } else {
        await this.fetchRateLimit(ratelimitId);
        await this.fetchOrg()
      }
    },
    watch: {
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
    },
    methods: {
      handleShowHowToPage(value){
        this.showHowToPage = value;
      },
      checkPermissions(){
        this.canChangeRateLimit = useStore.hasPermission("Can change rate limit configs");
        this.canAddRateLimit = useStore.hasPermission("Can add rate limit configs");
        this.canViewOrg = useStore.hasPermission("Can view organisation");
      },
      async fetchOrg(){
        try {
            this.orgs = await getOrgs();
            console.log(this.orgs)
        } catch (error) {
            
        }
      },
      async fetchRateLimit(ratelimitId) {
        this.loading = true;
        this.error = null;
        try {
            const rateLimits = await getRateLimitConfigs();
            this.ratelimit = rateLimits.find(item => item.id == ratelimitId)

        } catch (error) {
          console.error("Error fetching organization:", error);
          this.error =
            "An error occurred while fetching organization details. Please try again later.";
        } finally {
          this.loading = false;
        }
      },
      async saveRateLimit() {
        this.loading = true;
        this.error = null;
        
        try {
          const rateLimitData = this.formattedRateLimit;

          this.validateRatelimitData();
          if (Object.keys(this.fieldErrors).length > 0) return;
          
          if (this.isNewOrg) {
            const response = await createRateLimit(rateLimitData.organization,rateLimitData);
          } else {
            rateLimitData.organization = this.ratelimit.organization
            await updateRateLimit(this.ratelimit.id, rateLimitData);
          }
          this.$router.push({ name: "RateLimitsView" });
        } catch (error) {
          this.fieldErrors = {};
          const errData = error?.response?.data;
          if (errData) {
            for (const key in errData) {
              const value = errData[key];
  
              if (Array.isArray(value)) {
                this.fieldErrors[key] = value[0];
              } 
              else if (typeof value === 'object' && value !== null) {
                const nestedFirstKey = Object.keys(value)[0];
                const nestedMessages = value[nestedFirstKey];
  
                if (Array.isArray(nestedMessages)) {
                  this.fieldErrors[key] = nestedMessages[0];
                }
              }
            }
          }
  
          console.error("Error saving organization:", error);
        } finally {
          this.loading = false;
        }
      },
      async startTutorial() {
        const originalOrgs = [...this.orgs];
        const originalRateLimit = { ...this.ratelimit };

        this.orgs = [{ id: 1, name: 'Tutorial Organisation' }];

        await this._start(this.steps, () => {
          this.orgs = originalOrgs;
          this.ratelimit = { ...originalRateLimit };
        });
      },
      validateRatelimitData (){
        this.fieldErrors = {};
        if (this.ratelimit.organization === null){
          this.fieldErrors['organization'] = "Please select an organisation"
        }
        if (this.ratelimit.max_concurrent_updates === '' || isNaN(this.ratelimit.max_concurrent_updates )){
          this.fieldErrors['max_concurrent_updates'] = "Please specify a number for the max concurrent updates"
        }

      },
      clearFieldError(fieldName){
        if (this.fieldErrors[fieldName]) {
          delete this.fieldErrors[fieldName];
          delete this.fieldErrors['non_field_errors'];
        }
      },
    },
  };
  </script>
  